import React, { useContext, useState, useRef, useEffect, useCallback } from 'react';
import { AuthContext, API } from '@/App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { showToast } from '@/lib/custom-toast';
import {
  User as UserIcon, Mail, Shield, Camera, Eye, EyeOff, Crown, Gem, Zap,
  TrendingUp, Calendar, Award, Target, CreditCard, LogOut, HelpCircle, Bell,
  Clock, CheckCircle, AlertCircle, Loader2, ArrowRight, Sparkles, MessageCircle,
  ChevronRight, ChevronLeft, Flame, Landmark, Copy, Wallet, Scale,
  ArrowDownRight, ArrowUpRight, Tag
} from 'lucide-react';
import NotificationManager from '@/components/NotificationManager';
import { APP_VERSION } from '@/config/version';

const MobileSubHeader = ({ title, onBack }) => (
  <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-100 bg-white sticky top-0 z-10">
    <button
      type="button"
      onClick={onBack}
      className="p-2 rounded-full hover:bg-slate-100 active:bg-slate-200 transition-all -ml-1"
    >
      <ChevronLeft className="w-5 h-5 text-slate-700" />
    </button>
    <span className="text-base font-bold text-slate-900">{title}</span>
  </div>
);

const Profile = () => {
  const { user, token, login, logout, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'general');
  const [mobileSubView, setMobileSubView] = useState(null); // null = menú principal
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [stats, setStats] = useState(null);
  const [activitySummary, setActivitySummary] = useState(null);
  const [userAccounts, setUserAccounts] = useState([]);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    username: user?.username || '',
    nickname: user?.nickname || '',
    monthly_cut_day: user?.monthly_cut_day || 1,
    default_bank_name: user?.default_bank_name || '',
    default_clabe: user?.default_clabe || '',
    default_holder_name: user?.default_holder_name || '',
    current_password: '',
    new_password: '',
    confirm_password: '',
    profile_image: user?.profile_image || '',
    timezone: user?.timezone || 'America/Mexico_City'
  });

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  }, [token]);

  const fetchUserStats = useCallback(async () => {
    try {
      const [statsRes, actRes, accountsRes] = await Promise.all([
        axios.get(`${API}/statistics`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/profile/activity-summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/accounts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      if (actRes?.data) setActivitySummary(actRes.data);
      if (accountsRes?.data) setUserAccounts(accountsRes.data);
    } catch (error) {
      console.error('Error fetching stats or activity:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchUserStats();
    
    // Sincronizar tab desde URL si cambia
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }

    const handleResetTabs = () => {
      setActiveTab('general');
      setSearchParams({ tab: 'general' });
    };
    window.addEventListener('reset-profile-tabs', handleResetTabs);
    return () => window.removeEventListener('reset-profile-tabs', handleResetTabs);
  }, [fetchSubscriptionStatus, fetchUserStats, searchParams, activeTab, setSearchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const handleManageSubscription = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/create-portal-session`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.portal_url;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al abrir portal de gestión';
      showToast.error(errorMessage);
      setActionLoading(false);
    }
  };

  const handleSyncStatus = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/sync-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success(response.data.message);
      await refreshUser();
      await fetchSubscriptionStatus();
    } catch (error) {
      showToast.error('Error al sincronizar con Stripe');
    } finally {
      setActionLoading(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      setFormData(prev => ({ ...prev, profile_image: base64 }));
      // Auto-guardar imagen en el backend
      try {
        setLoading(true);
        const response = await axios.put(`${API}/profile`, {
          username: formData.username,
          timezone: formData.timezone,
          profile_image: base64
        }, { headers: { Authorization: `Bearer ${token}` } });
        login(token, response.data);
        showToast.success('Foto actualizada');
      } catch {
        showToast.error('Error al guardar la foto');
      } finally {
        setLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (formData.new_password) {
      if (!formData.current_password) {
        showToast.error('Ingresa tu contraseña actual para cambiarla');
        setLoading(false);
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        showToast.error('Las contraseñas nuevas no coinciden');
        setLoading(false);
        return;
      }
      if (formData.new_password.length < 6) {
        showToast.error('La nueva contraseña debe tener al menos 6 caracteres');
        setLoading(false);
        return;
      }
    }

    const cleanClabe = (formData.default_clabe || '').replace(/\s/g, '');
    if (cleanClabe && cleanClabe.length !== 18) {
      showToast.error('La CLABE interbancaria debe tener exactamente 18 dígitos');
      setLoading(false);
      return;
    }

    try {
      const updateData = {
        username: formData.username,
        timezone: formData.timezone,
        nickname: formData.nickname,
        monthly_cut_day: parseInt(formData.monthly_cut_day, 10) || 1,
        default_bank_name: formData.default_bank_name,
        default_clabe: cleanClabe,
        default_holder_name: formData.default_holder_name
      };

      if (formData.profile_image !== user?.profile_image) {
        updateData.profile_image = formData.profile_image;
      }

      if (formData.new_password) {
        updateData.current_password = formData.current_password;
        updateData.new_password = formData.new_password;
      }

      const response = await axios.put(`${API}/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      login(token, response.data);
      showToast.success('Perfil actualizado exitosamente');

      setFormData({
        ...formData,
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al actualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    showToast.success('¡Hasta pronto!');
  };

  const tier = user?.subscription_tier || 'free';
  const status = user?.subscription_status || subscription?.subscription_status;
  const isActive = status === 'active' || status === 'trialing';
  const effectiveTier = isActive ? tier : 'free';
  const isFree = effectiveTier === 'free';
  const isPremium = effectiveTier === 'premium';
  const isPro = effectiveTier === 'pro';

  const tierConfig = {
    free: { name: 'Gratis', icon: Zap, color: 'slate', gradient: 'from-slate-500 to-slate-600' },
    premium: { name: 'Premium', icon: Crown, color: 'blue', gradient: 'from-blue-500 to-indigo-600' },
    pro: { name: 'Pro', icon: Gem, color: 'purple', gradient: 'from-purple-500 to-pink-600' }
  };

  const currentTier = tierConfig[effectiveTier] || tierConfig.free;

  const plans = {
    free: {
      name: 'Gratis',
      icon: Zap,
      features: [
        'Transacciones manuales ilimitadas',
        'Categorías personalizadas',
        'Dashboard con estadísticas',
        'Transacciones recurrentes',
        'Registro de inversiones'
      ]
    },
    premium: {
      name: 'Premium',
      icon: Crown,
      features: [
        '🎙️ 100 transacciones por voz/mes',
        '🤖 IA financiera + Chat inteligente',
        '🎯 Objetivos financieros',
        '📊 Análisis predictivos',
        '🔔 Notificaciones automáticas',
        '🚫 Sin publicidad'
      ]
    },
    pro: {
      name: 'Pro',
      icon: Gem,
      features: [
        '🎙️ 300 transacciones por voz/mes',
        '📄 Reportes PDF exportables',
        '🚀 Soporte prioritario',
        '📈 Análisis avanzados',
        '📊 Exportar a Excel/CSV',
        '⚡ Acceso anticipado a funciones'
      ]
    }
  };

  const getActiveFeatures = () => {
    if (isPro) return [...plans.free.features, ...plans.premium.features, ...plans.pro.features];
    if (isPremium) return [...plans.free.features, ...plans.premium.features];
    return plans.free.features;
  };

  const tabs = [
    { id: 'general', label: 'General', icon: UserIcon },
    { id: 'security', label: 'Seguridad', icon: Shield },
    { id: 'subscription', label: 'Plan', icon: CreditCard },
    { id: 'notifications', label: 'Notificaciones', icon: Bell },
    { id: 'support', label: 'Soporte', icon: HelpCircle }
  ];

  const daysInApp = Math.floor((Date.now() - new Date(user?.created_at || Date.now()).getTime()) / (1000 * 60 * 60 * 24));

  // ─── MOBILE PROFILE VIEW ───────────────────────────────────────────────────
  const renderMobileProfileView = () => {
    // Sub-vista: Cuenta
    if (mobileSubView === 'cuenta') return (
      <div className="flex flex-col bg-slate-50">
        <MobileSubHeader title="Cuenta y Datos" onBack={() => setMobileSubView(null)} />
        <div className="p-4 space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nombre de usuario */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider px-1">Nombre de usuario</label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={formData.username}
                  disabled
                  className="w-full pl-10 pr-4 py-3 border border-dashed border-slate-200 rounded-2xl bg-slate-50 text-slate-500 font-medium cursor-not-allowed text-sm"
                />
              </div>
              <p className="text-[11px] text-slate-400 px-1">El nombre de usuario no se puede cambiar. Usa tu Alias para personalizar tu nombre.</p>
            </div>

            {/* Alias / Apodo opcional */}
            <div className="space-y-1">
              <div className="flex items-center justify-between px-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Alias / Apodo</label>
                <span className="text-[10px] text-slate-400 font-medium">Opcional</span>
              </div>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                  placeholder="Ej: Juancho, Lic. Pérez"
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-2xl bg-white text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider px-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input value={user?.email || ''} disabled className="w-full pl-10 pr-4 py-3 border border-dashed border-slate-200 rounded-2xl bg-slate-50 text-slate-400 font-medium text-sm" />
              </div>
            </div>

            {/* Zona horaria */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider px-1">Zona horaria</label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-2xl bg-white text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none text-sm"
                >
                  <option value="America/Mexico_City">Ciudad de México (UTC-6)</option>
                  <option value="America/Bogota">Bogotá (UTC-5)</option>
                  <option value="America/Argentina/Buenos_Aires">Buenos Aires (UTC-3)</option>
                  <option value="Europe/Madrid">Madrid (UTC+1)</option>
                </select>
              </div>
            </div>

            {/* Día de corte mensual */}
            <div className="space-y-1 pt-1">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider px-1">Día de corte mensual</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 1, label: 'Día 1' },
                  { value: 15, label: 'Día 15' },
                  { value: 'custom', label: 'Otro día' }
                ].map(opt => {
                  const isSelected = opt.value === 'custom'
                    ? (formData.monthly_cut_day !== 1 && formData.monthly_cut_day !== 15)
                    : (formData.monthly_cut_day === opt.value);
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => {
                        if (opt.value !== 'custom') {
                          setFormData({ ...formData, monthly_cut_day: opt.value });
                        } else if (formData.monthly_cut_day === 1 || formData.monthly_cut_day === 15) {
                          setFormData({ ...formData, monthly_cut_day: 5 });
                        }
                      }}
                      className={`py-2.5 px-2 rounded-xl text-xs font-bold transition-all border ${
                        isSelected
                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {formData.monthly_cut_day !== 1 && formData.monthly_cut_day !== 15 && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-600">Día exacto (1 al 28):</span>
                    <input
                      type="number"
                      min="1"
                      max="28"
                      value={formData.monthly_cut_day}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!isNaN(v)) setFormData({ ...formData, monthly_cut_day: Math.max(1, Math.min(28, v)) });
                      }}
                      className="w-20 px-3 py-1.5 border border-slate-200 rounded-xl bg-white font-bold text-center text-sm"
                    />
                  </div>
                </div>
              )}
              <p className="text-[11px] text-slate-400 px-1 leading-relaxed mt-1">
                Define el inicio de tu ciclo mensual para estadísticas y presupuestos.
              </p>
            </div>

            {/* Sección SPEI Predeterminado */}
            <div className="pt-2">
              <div className="p-4 bg-white rounded-2xl border border-slate-200 space-y-3 shadow-xs">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                    <Landmark className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Datos Bancarios SPEI</h4>
                    <p className="text-[11px] text-slate-400">Para tus ligas de cobro públicas</p>
                  </div>
                </div>

                {userAccounts.filter(a => a.clabe).length > 0 && (
                  <div className="pt-1">
                    <select
                      onChange={(e) => {
                        const acc = userAccounts.find(a => a.id === e.target.value);
                        if (acc) {
                          setFormData(prev => ({
                            ...prev,
                            default_bank_name: acc.bank_name || acc.name || '',
                            default_clabe: acc.clabe || '',
                            default_holder_name: acc.holder_name || user?.username || ''
                          }));
                          showToast.success(`Datos cargados desde "${acc.name}"`);
                        }
                      }}
                      defaultValue=""
                      className="w-full text-xs font-semibold py-2 px-3 bg-blue-50/80 text-blue-700 rounded-xl border border-blue-200 cursor-pointer"
                    >
                      <option value="" disabled>⚡ Copiar datos de una de mis cuentas...</option>
                      {userAccounts.filter(a => a.clabe).map(a => (
                        <option key={a.id} value={a.id}>
                          {a.name} ({a.bank_name || 'SPEI'} - ...{a.clabe.slice(-4)})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="space-y-2.5 pt-1">
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 uppercase">Titular de la Cuenta</label>
                    <input
                      value={formData.default_holder_name}
                      onChange={(e) => setFormData({ ...formData, default_holder_name: e.target.value })}
                      placeholder="Nombre completo del titular"
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-slate-50/50 text-slate-900 text-xs font-medium focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 mt-0.5"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 uppercase">Banco</label>
                    <input
                      value={formData.default_bank_name}
                      onChange={(e) => setFormData({ ...formData, default_bank_name: e.target.value })}
                      placeholder="Ej: BBVA, Nu, Santander"
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-slate-50/50 text-slate-900 text-xs font-medium focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 mt-0.5"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 uppercase">CLABE Interbancaria (18 dígitos)</label>
                    <input
                      value={formData.default_clabe}
                      onChange={(e) => setFormData({ ...formData, default_clabe: e.target.value.replace(/[^0-9]/g, '').slice(0, 18) })}
                      placeholder="012345678901234567"
                      maxLength={18}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-slate-50/50 text-slate-900 text-xs font-mono tracking-wider focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 mt-0.5"
                    />
                  </div>
                  <p className="text-[10px] text-slate-400 leading-tight">
                    Tus datos para recibir pagos por transferencia en links de cobro ("Me Deben"). Se autollenarán sin tener que escribir tu CLABE cada vez.
                  </p>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl shadow-lg shadow-blue-200 active:scale-95 transition-all text-sm mt-2"
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </form>
        </div>
      </div>
    );

    // Sub-vista: Seguridad
    if (mobileSubView === 'seguridad') return (
      <div className="flex flex-col bg-slate-50">
        <MobileSubHeader title="Seguridad" onBack={() => setMobileSubView(null)} />
        <div className="p-4 space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider px-1">Contraseña actual</label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.current_password}
                  onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-2xl bg-white font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider px-1">Nueva contraseña</label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.new_password}
                  onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-2xl bg-white font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider px-1">Confirmar nueva contraseña</label>
              <input
                type="password"
                placeholder="••••••••"
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-2xl bg-white font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-2xl active:scale-95 transition-all"
            >
              {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
            </button>
          </form>
        </div>
      </div>
    );

    // Sub-vista: Plan
    if (mobileSubView === 'plan') return (
      <div className="flex flex-col bg-slate-50">
        <MobileSubHeader title="Mi Plan" onBack={() => setMobileSubView(null)} />
        <div className="p-4 space-y-4">
          <div className={`p-6 rounded-3xl border-2 ${
            isPro ? 'border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50' :
            isPremium ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50' :
            'border-slate-200 bg-white'
          }`}>
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-14 h-14 bg-gradient-to-br ${currentTier.gradient} rounded-2xl flex items-center justify-center shadow-lg`}>
                <currentTier.icon className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Plan actual</p>
                <h3 className="text-2xl font-black text-slate-900">{currentTier.name}</h3>
              </div>
            </div>
            {isActive && <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold uppercase"><CheckCircle className="w-3 h-3" /> Activo</span>}
            {!isActive && user?.subscription_status === 'past_due' && <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold uppercase"><AlertCircle className="w-3 h-3" /> Pago pendiente</span>}
            {!isActive && user?.subscription_status !== 'past_due' && <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-bold uppercase">Plan Gratuito</span>}
          </div>

          {!isActive && (
            <button
              onClick={user?.subscription_status === 'past_due' ? handleManageSubscription : () => navigate('/pricing')}
              disabled={actionLoading}
              className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-2xl shadow-sm active:scale-95 transition-transform duration-100 flex items-center justify-center gap-2 text-sm"
            >
              <CreditCard className="w-4 h-4" />
              {user?.subscription_status === 'past_due' ? 'Pagar factura pendiente' : '🚀 Obtener Pro'}
            </button>
          )}

          <div className="bg-white rounded-3xl border border-slate-100 p-4 space-y-3">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Funciones activas</p>
            {getActiveFeatures().map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle className="w-4 h-4 text-blue-500 shrink-0" />
                <p className="text-sm text-slate-700">{feature}</p>
              </div>
            ))}
          </div>

          {(isPremium || isPro || user?.has_payment_method) ? (
            <button onClick={handleManageSubscription} disabled={actionLoading} className="w-full py-4 bg-white border border-slate-200 text-slate-900 font-bold rounded-2xl shadow-sm active:scale-95 transition-all flex items-center justify-center gap-2">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              Gestionar en Stripe
            </button>
          ) : (
            <button onClick={() => navigate('/pricing')} className="w-full py-4 bg-blue-600 text-white font-bold rounded-2xl shadow-lg shadow-blue-200 active:scale-95 transition-all">
              🚀 Ver Planes Premium / Pro
            </button>
          )}

          {/* Botón Sincronizar Pago SIEMPRE visible en móvil */}
          <button
            onClick={handleSyncStatus}
            disabled={actionLoading}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-2xl shadow-lg shadow-blue-200 active:scale-95 transition-all flex items-center justify-center gap-2 text-sm"
          >
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            <span>Sincronizar Pago con Stripe</span>
          </button>
        </div>
      </div>
    );

    // Sub-vista: Notificaciones
    if (mobileSubView === 'notificaciones') return (
      <div className="flex flex-col bg-slate-50">
        <MobileSubHeader title="Notificaciones" onBack={() => setMobileSubView(null)} />
        <div className="p-4">
          <NotificationManager />
        </div>
      </div>
    );

    // Sub-vista: Actividad
    if (mobileSubView === 'actividad') {
      const act = activitySummary || {};
      const monthlyInc = act.monthly_income ?? stats?.total_income ?? 0;
      const monthlyExp = act.monthly_expenses ?? stats?.total_expenses ?? 0;
      const monthlyBal = act.monthly_balance ?? (monthlyInc - monthlyExp);
      const streak = act.streak_days ?? 0;
      const goalsProg = act.goals_progress_percent ?? 0;
      const activeGoals = act.active_goals_count ?? stats?.active_goals ?? 0;
      const bankCount = act.bank_accounts_count ?? userAccounts.filter(a => a.type !== 'credit_card').length;
      const cardCount = act.credit_cards_count ?? userAccounts.filter(a => a.type === 'credit_card').length;
      const debtsCollect = act.debts_to_collect ?? 0;
      const debtsPay = act.debts_to_pay ?? 0;
      const days = act.days_in_app ?? daysInApp;

      const formatMoney = (val) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val);

      return (
        <div className="flex flex-col bg-slate-50 min-h-screen">
          <MobileSubHeader title="Resumen de Actividad" onBack={() => setMobileSubView(null)} />
          <div className="p-4 space-y-3 pb-8">
            {/* 1. Flujo del Mes */}
            <div className="bg-slate-900 rounded-3xl p-5 text-white shadow-md relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl"></div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Flujo del Mes Actual</span>
                <span className={`text-xs font-black px-2.5 py-0.5 rounded-full ${monthlyBal >= 0 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'}`}>
                  {monthlyBal >= 0 ? 'Balance +' : 'Balance -'}
                </span>
              </div>
              <p className={`text-3xl font-black ${monthlyBal >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {formatMoney(monthlyBal)}
              </p>
              <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-slate-800">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-emerald-500/20 rounded-lg text-emerald-400"><ArrowDownRight className="w-4 h-4" /></div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase font-semibold">Ingresos</p>
                    <p className="text-sm font-bold text-emerald-400">{formatMoney(monthlyInc)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-rose-500/20 rounded-lg text-rose-400"><ArrowUpRight className="w-4 h-4" /></div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase font-semibold">Gastos</p>
                    <p className="text-sm font-bold text-rose-400">{formatMoney(monthlyExp)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Racha de días seguidos */}
            <div className="bg-white rounded-3xl p-4 border border-slate-200/80 shadow-xs flex items-center justify-between">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Flame className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-base font-black text-slate-900">{streak} {streak === 1 ? 'día' : 'días'} seguidos</h4>
                  <p className="text-xs text-slate-400 font-medium">Racha registrando movimientos</p>
                </div>
              </div>
              <span className="text-xl">🔥</span>
            </div>

            {/* 3. Metas de ahorro */}
            <div className="bg-white rounded-3xl p-4 border border-slate-200/80 shadow-xs space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-xl"><Target className="w-5 h-5" /></div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Metas de Ahorro</h4>
                    <p className="text-[11px] text-slate-400">{activeGoals} {activeGoals === 1 ? 'meta activa' : 'metas activas'}</p>
                  </div>
                </div>
                <span className="text-sm font-black text-blue-600">{goalsProg}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${Math.min(100, goalsProg)}%` }}></div>
              </div>
            </div>

            {/* 4. Cuentas y Tarjetas bajo control */}
            <div className="bg-white rounded-3xl p-4 border border-slate-200/80 shadow-xs">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="p-2 bg-purple-50 text-purple-600 rounded-xl"><Wallet className="w-5 h-5" /></div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Cuentas y Tarjetas</h4>
                  <p className="text-[11px] text-slate-400">Patrimonio bajo control</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="p-2.5 bg-slate-50 rounded-2xl border border-slate-100">
                  <p className="text-lg font-black text-slate-900">{bankCount}</p>
                  <p className="text-[10px] font-bold text-slate-500 uppercase">Cuentas Débito/Efectivo</p>
                </div>
                <div className="p-2.5 bg-slate-50 rounded-2xl border border-slate-100">
                  <p className="text-lg font-black text-slate-900">{cardCount}</p>
                  <p className="text-[10px] font-bold text-slate-500 uppercase">Tarjetas de Crédito</p>
                </div>
              </div>
            </div>

            {/* 5. Balance de deudas */}
            <div className="bg-white rounded-3xl p-4 border border-slate-200/80 shadow-xs">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl"><Scale className="w-5 h-5" /></div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Balance de Deudas</h4>
                  <p className="text-[11px] text-slate-400">Por cobrar vs Por pagar</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2.5 bg-emerald-50/50 rounded-2xl border border-emerald-100/60">
                  <p className="text-[10px] font-bold text-emerald-700 uppercase">Me Deben</p>
                  <p className="text-base font-black text-emerald-600">{formatMoney(debtsCollect)}</p>
                </div>
                <div className="p-2.5 bg-rose-50/50 rounded-2xl border border-rose-100/60">
                  <p className="text-[10px] font-bold text-rose-700 uppercase">Debo</p>
                  <p className="text-base font-black text-rose-600">{formatMoney(debtsPay)}</p>
                </div>
              </div>
            </div>

            {/* 6. Días en la App */}
            <div className="bg-white rounded-3xl p-4 border border-slate-200/80 shadow-xs flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Award className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">{days} días en PGastos</p>
                  <p className="text-[11px] text-slate-400 font-medium">Transformando tu salud financiera</p>
                </div>
              </div>
              <span className="text-xs font-black text-slate-400">#{stats?.transaction_count || 0} movs</span>
            </div>
          </div>
        </div>
      );
    }

    // Sub-vista: Soporte
    if (mobileSubView === 'soporte') return (
      <div className="flex flex-col bg-slate-50">
        <MobileSubHeader title="Soporte" onBack={() => setMobileSubView(null)} />
        <div className="p-4 space-y-3">
          {[
            { label: 'Reportar un error', sub: 'Algo no funciona bien', icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50', action: () => navigate('/support?type=bug') },
            { label: 'Sugerir una mejora', sub: 'Tengo una idea increíble', icon: Sparkles, color: 'text-amber-500', bg: 'bg-amber-50', action: () => navigate('/support?type=suggestion') },
            { label: 'Enviar queja', sub: 'No estoy satisfecho', icon: Target, color: 'text-purple-500', bg: 'bg-purple-50', action: () => navigate('/feedback?type=complaint') },
            { label: 'Otros asuntos', sub: 'Cualquier otra duda', icon: HelpCircle, color: 'text-slate-500', bg: 'bg-slate-100', action: () => navigate('/feedback?type=other') },
          ].map(({ label, sub, icon: Icon, color, bg, action }) => (
            <button key={label} onClick={action} className="w-full flex items-center justify-between p-4 bg-white rounded-2xl border border-slate-100 active:scale-[0.98] transition-all shadow-sm">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 ${bg} rounded-xl`}><Icon className={`w-5 h-5 ${color}`} /></div>
                <div className="text-left">
                  <p className="text-sm font-bold text-slate-800">{label}</p>
                  <p className="text-xs text-slate-400">{sub}</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </button>
          ))}
          <div
            className="w-full p-5 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl text-white flex items-center justify-between shadow-xl shadow-blue-200 active:scale-[0.98] transition-all cursor-pointer"
            onClick={() => window.location.href = `mailto:soporte@capacitatu.com?subject=Soporte VIP PGastos - ${user?.username || 'Usuario'}`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center"><Gem className="w-6 h-6 text-white" /></div>
              <div>
                <p className="font-black text-base">Soporte VIP</p>
                <p className="text-blue-100 text-xs">Atención directa por email</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5" />
          </div>
        </div>
      </div>
    );

    // ─── MENÚ PRINCIPAL MÓVIL ───
    const menuItems = [
      { id: 'cuenta', label: 'Cuenta y Datos', sub: 'Nombre de usuario, zona horaria', icon: UserIcon, iconBg: 'bg-blue-500', iconColor: 'text-white' },
      { id: 'seguridad', label: 'Seguridad', sub: 'Contraseña de acceso', icon: Shield, iconBg: 'bg-green-500', iconColor: 'text-white' },
      { id: 'plan', label: `Mi Plan`, sub: currentTier.name, icon: CreditCard, iconBg: 'bg-indigo-500', iconColor: 'text-white' },
      { id: 'notificaciones', label: 'Notificaciones', sub: 'Alertas y recordatorios', icon: Bell, iconBg: 'bg-amber-500', iconColor: 'text-white' },
      { id: 'actividad', label: 'Mi Actividad', sub: `${stats?.transaction_count || 0} transacciones · ${daysInApp} días`, icon: TrendingUp, iconBg: 'bg-slate-600', iconColor: 'text-white' },
      { id: 'soporte', label: 'Soporte', sub: 'Reportar errores, sugerencias', icon: HelpCircle, iconBg: 'bg-purple-500', iconColor: 'text-white' },
    ];

    return (
      <div className="flex flex-col bg-slate-100 pb-6">

        {/* ── Hero compacto horizontal (estilo Nubank/Mercado Pago) ── */}
        <div className="bg-white px-5 pt-5 pb-5 border-b border-slate-100">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                {formData.profile_image ? (
                  <img src={formData.profile_image} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <UserIcon className="w-8 h-8 text-white" />
                )}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-0.5 -right-0.5 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center shadow-md active:scale-90 transition-all"
              >
                <Camera className="w-3 h-3 text-white" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-slate-900 truncate">
                {formData.nickname ? formData.nickname : user?.username}
              </h2>
              {formData.nickname && (
                <p className="text-xs font-semibold text-blue-600 truncate">@{user?.username}</p>
              )}
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              <div className={`inline-flex items-center gap-1 mt-1.5 px-2.5 py-0.5 bg-gradient-to-r ${currentTier.gradient} rounded-full`}>
                <currentTier.icon className="w-3 h-3 text-white" />
                <span className="text-white text-[11px] font-bold">{currentTier.name}</span>
              </div>
            </div>

            {/* Stats chips */}
            <div className="flex flex-col items-end gap-1 shrink-0">
              <div className="bg-slate-50 rounded-xl px-3 py-2 text-center">
                <p className="text-base font-black text-slate-900 leading-none">{stats?.transaction_count || 0}</p>
                <p className="text-[10px] text-slate-400 font-medium leading-tight">movimientos</p>
              </div>
              <div className="bg-slate-50 rounded-xl px-3 py-2 text-center">
                <p className="text-base font-black text-slate-900 leading-none">{daysInApp}</p>
                <p className="text-[10px] text-slate-400 font-medium leading-tight">días en app</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Sección: Configuración ── */}
        <div className="mt-5 mx-4">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">Configuración</p>
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
            {menuItems.slice(0, 3).map(({ id, label, sub, icon: Icon, iconBg, iconColor }, i) => (
              <button
                key={id}
                onClick={() => { setMobileSubView(id); window.scrollTo(0, 0); }}
                className={`w-full flex items-center justify-between px-4 py-3.5 active:bg-slate-50 transition-colors ${i < 2 ? 'border-b border-slate-50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 ${iconBg} rounded-xl flex items-center justify-center shrink-0`}>
                    <Icon className={`w-4.5 h-4.5 ${iconColor}`} style={{width:'18px',height:'18px'}} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-slate-800">{label}</p>
                    <p className="text-xs text-slate-400">{sub}</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* ── Sección: Cuenta ── */}
        <div className="mt-4 mx-4">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">Cuenta</p>
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
            {menuItems.slice(3).map(({ id, label, sub, icon: Icon, iconBg, iconColor }, i) => (
              <button
                key={id}
                onClick={() => { setMobileSubView(id); window.scrollTo(0, 0); }}
                className={`w-full flex items-center justify-between px-4 py-3.5 active:bg-slate-50 transition-colors ${i < 2 ? 'border-b border-slate-50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 ${iconBg} rounded-xl flex items-center justify-center shrink-0`}>
                    <Icon className={`w-4.5 h-4.5 ${iconColor}`} style={{width:'18px',height:'18px'}} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-slate-800">{label}</p>
                    <p className="text-xs text-slate-400">{sub}</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* ── Cerrar Sesión ── */}
        <div className="mt-4 mx-4">
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-red-50 transition-colors"
            >
              <div className="w-9 h-9 bg-red-500 rounded-xl flex items-center justify-center shrink-0">
                <LogOut className="w-4 h-4 text-white" style={{width:'18px',height:'18px'}} />
              </div>
              <p className="text-sm font-semibold text-red-600">Cerrar Sesión</p>
            </button>
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-300 mt-6">PGastos · v{APP_VERSION}</p>
      </div>
    );
  };



  return (
    <Layout>
      {/* Vista Móvil */}
      <div className="lg:hidden">
        {renderMobileProfileView()}
      </div>

      {/* Vista Escritorio */}
      <div className="hidden lg:block max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Header Hero Section */}
        <div className="relative overflow-hidden bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm mb-8">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full -mr-32 -mt-32 opacity-50 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-50 rounded-full -ml-32 -mb-32 opacity-50 blur-3xl"></div>
          
          <div className="relative flex flex-col md:flex-row items-center gap-8">
            <div className="relative group">
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 p-1">
                <div className="w-full h-full rounded-full overflow-hidden bg-white">
                  {formData.profile_image ? (
                    <img src={formData.profile_image} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600">
                      <UserIcon className="w-16 h-16 text-white" />
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-1 right-1 p-2.5 bg-white border border-slate-200 rounded-full text-blue-600 hover:text-blue-700 shadow-xl transition-all hover:scale-110 active:scale-95"
              >
                <Camera className="w-5 h-5" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
            </div>

            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-col md:flex-row md:items-center gap-3 mb-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-3xl font-extrabold text-slate-900">
                    {formData.nickname ? formData.nickname : user?.username}
                  </h1>
                  {formData.nickname && (
                    <span className="text-sm font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
                      @{user?.username}
                    </span>
                  )}
                </div>
                <button 
                  onClick={() => handleTabChange('subscription')}
                  className={`inline-flex items-center gap-2 px-4 py-1 bg-gradient-to-r ${currentTier.gradient} rounded-full text-white text-sm font-bold w-fit mx-auto md:mx-0 shadow-md hover:scale-105 transition-all cursor-pointer border-none outline-none`}
                >
                  <currentTier.icon className="w-4 h-4" />
                  {currentTier.name}
                </button>
              </div>
              <div className="flex flex-wrap justify-center md:justify-start gap-4 text-slate-600 text-sm">
                <div className="flex items-center gap-1.5"><Mail className="w-4 h-4" /> {user?.email}</div>
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" /> 
                  Miembro desde {new Date(user?.created_at || Date.now()).toLocaleDateString('es-MX', { year: 'numeric', month: 'long' })}
                </div>
              </div>
            </div>

            <div className="mt-4 md:mt-0 md:ml-auto flex justify-center md:justify-end">
              <Button onClick={handleLogout} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 font-bold gap-2 shadow-sm rounded-xl">
                <LogOut className="w-4 h-4" />
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center p-1 bg-slate-100 rounded-2xl w-fit mx-auto sm:mx-0 mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActiveTab = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex items-center gap-2 px-4 sm:px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300
                  ${isActiveTab 
                    ? 'bg-white text-blue-600 shadow-md' 
                    : 'text-slate-500 hover:text-slate-800'
                  }
                `}
              >
                <Icon className={`w-4 h-4 ${isActiveTab ? 'text-blue-600' : 'text-slate-400'}`} />
                <span className="hidden sm:block">{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8 min-h-[500px]">
            {activeTab === 'general' && (
              <Card className="p-6 border-slate-200 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-xl font-bold text-slate-900 mb-6">Información General</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="username">Nombre de Usuario</Label>
                      <div className="relative">
                        <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input id="username" value={formData.username} disabled className="pl-10 bg-slate-50 text-slate-500 border-dashed cursor-not-allowed" />
                      </div>
                      <p className="text-xs text-slate-400">El nombre de usuario no se puede cambiar. Usa tu Alias para personalizar cómo se muestra tu nombre.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Email Personal</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input value={user?.email || ''} disabled className="pl-10 bg-slate-50 text-slate-500 border-dashed" />
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="nickname" className="flex items-center gap-1.5">
                        <Tag className="w-3.5 h-3.5 text-blue-500" />
                        <span>Alias / Apodo (opcional)</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="nickname"
                          placeholder="Ej: Victor Ramos"
                          value={formData.nickname}
                          onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                          className="bg-white"
                        />
                      </div>
                      <p className="text-xs text-slate-400">Nombre con el que te saludará la app y se mostrará en tu perfil.</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="monthly_cut_day" className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-indigo-500" />
                        <span>Día de Corte Mensual</span>
                      </Label>
                      <div className="flex gap-2 items-center">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, monthly_cut_day: 1 })}
                          className={`flex-1 py-2 px-3 text-xs font-bold rounded-lg border transition-all ${Number(formData.monthly_cut_day) === 1 ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                        >
                          Día 1 (Mes natural)
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, monthly_cut_day: 15 })}
                          className={`flex-1 py-2 px-3 text-xs font-bold rounded-lg border transition-all ${Number(formData.monthly_cut_day) === 15 ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                        >
                          Día 15 (Quincenal)
                        </button>
                        <div className="flex items-center gap-1 w-28">
                          <Input
                            type="number"
                            min="1"
                            max="28"
                            value={formData.monthly_cut_day}
                            onChange={(e) => setFormData({ ...formData, monthly_cut_day: Math.min(28, Math.max(1, parseInt(e.target.value) || 1)) })}
                            className="text-center font-bold"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-400">Define el ciclo base mensual para reportes y presupuestos (1-28).</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timezone">Preferencia Horaria</Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <select
                        id="timezone"
                        value={formData.timezone}
                        onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                        className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                      >
                        <option value="America/Mexico_City">Ciudad de México (UTC-6)</option>
                        <option value="America/Bogota">Bogotá (UTC-5)</option>
                        <option value="America/Argentina/Buenos_Aires">Buenos Aires (UTC-3)</option>
                        <option value="Europe/Madrid">Madrid (UTC+1)</option>
                      </select>
                    </div>
                  </div>

                  {/* ── Datos Bancarios / SPEI Predeterminados ── */}
                  <div className="pt-4 border-t border-slate-100 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                          <Landmark className="w-4 h-4 text-indigo-600" />
                          Datos Bancarios para Transferencias (SPEI)
                        </h3>
                        <p className="text-xs text-slate-400">Guarda tu cuenta predeterminada para compartirla al cobrar o transferirte.</p>
                      </div>
                      {userAccounts && userAccounts.filter(a => a.clabe || a.account_number).length > 0 && (
                        <select
                          className="text-xs bg-indigo-50 text-indigo-700 font-semibold border border-indigo-200 rounded-lg px-2.5 py-1.5 focus:outline-none"
                          onChange={(e) => {
                            const acc = userAccounts.find(a => a.id === e.target.value);
                            if (acc) {
                              setFormData(prev => ({
                                ...prev,
                                default_bank_name: acc.institution_name || acc.name || prev.default_bank_name,
                                default_clabe: acc.clabe || acc.account_number || prev.default_clabe,
                                default_holder_name: acc.holder_name || prev.default_holder_name || prev.nickname || prev.username
                              }));
                            }
                          }}
                          defaultValue=""
                        >
                          <option value="" disabled>Importar de cuenta registrada...</option>
                          {userAccounts.filter(a => a.clabe || a.account_number).map(acc => (
                            <option key={acc.id} value={acc.id}>
                              {acc.name} ({acc.institution_name || 'Banco'})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="default_holder_name" className="text-xs">Nombre del Titular</Label>
                        <Input
                          id="default_holder_name"
                          placeholder="Ej: Victor Ramos"
                          value={formData.default_holder_name}
                          onChange={(e) => setFormData({ ...formData, default_holder_name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="default_bank_name" className="text-xs">Banco / Institución</Label>
                        <Input
                          id="default_bank_name"
                          placeholder="Ej: BBVA, Nu, Banamex"
                          value={formData.default_bank_name}
                          onChange={(e) => setFormData({ ...formData, default_bank_name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="default_clabe" className="text-xs">CLABE Interbancaria (18 dígitos)</Label>
                        <Input
                          id="default_clabe"
                          placeholder="18 dígitos"
                          maxLength={18}
                          value={formData.default_clabe}
                          onChange={(e) => setFormData({ ...formData, default_clabe: e.target.value.replace(/\D/g, '') })}
                          className="font-mono text-xs"
                        />
                      </div>
                    </div>
                  </div>

                  <Button type="submit" disabled={loading} className="w-full sm:w-auto px-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-6 h-auto font-bold shadow-lg shadow-blue-200 transition-all active:scale-95">
                    {loading ? 'Sincronizando...' : 'Guardar Perfil'}
                  </Button>
                </form>
              </Card>
            )}

            {activeTab === 'security' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <Card className="p-6 border-slate-200">
                  <h2 className="text-xl font-bold text-slate-900 mb-6">Seguridad de la Cuenta</h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Contraseña Actual</Label>
                      <div className="relative">
                        <Input type={showCurrentPassword ? "text" : "password"} placeholder="••••••••" value={formData.current_password} onChange={(e) => setFormData({ ...formData, current_password: e.target.value })} />
                        <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nueva Contraseña</Label>
                        <div className="relative">
                          <Input type={showNewPassword ? "text" : "password"} placeholder="••••••••" value={formData.new_password} onChange={(e) => setFormData({ ...formData, new_password: e.target.value })} />
                          <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Confirmar</Label>
                        <Input type="password" placeholder="••••••••" value={formData.confirm_password} onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })} />
                      </div>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-xl py-4 h-auto font-bold transition-all">
                      Actualizar Credenciales
                    </Button>
                  </form>
                </Card>

                <Card className="p-6 border-red-100 bg-red-50/30">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-red-100 rounded-xl"><LogOut className="w-6 h-6 text-red-600" /></div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900">Sesión Activa</h3>
                      <p className="text-sm text-slate-600 mb-4">Si compartes este dispositivo, recuerda cerrar tu sesión al terminar.</p>
                      <Button onClick={handleLogout} variant="outline" className="border-red-200 text-red-600 hover:bg-red-600 hover:text-white font-bold px-8">
                        Cerrar Sesión Ahora
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {activeTab === 'subscription' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10">
                {/* Status Card */}
                <Card className={`p-6 sm:p-8 border-2 ${isPro ? 'border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50' :
                  isPremium ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50' :
                  'border-slate-200 bg-white'
                }`}>
                  <div className="flex flex-col sm:flex-row items-start justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-16 h-16 bg-gradient-to-br ${plans[tier].gradient || currentTier.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                          <currentTier.icon className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Plan {currentTier.name}</h2>
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            {isActive ? (
                              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold uppercase">
                                <CheckCircle className="w-3 h-3" /> Activo
                              </span>
                            ) : !isFree && (
                              <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold uppercase">
                                <AlertCircle className="w-3 h-3" /> Expirado
                              </span>
                            )}
                            {isFree && (
                              <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-bold uppercase">Básico</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {subscription?.subscription_end_date && (
                        <div className="flex items-center gap-2 text-slate-600 text-sm">
                          <Calendar className="w-4 h-4" />
                          <span>Próximo cobro: {new Date(subscription.subscription_end_date).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2.5 w-full sm:w-auto">
                      {(isPremium || isPro || user?.has_payment_method) && (
                        <Button onClick={handleManageSubscription} disabled={actionLoading} className="bg-white text-slate-900 border border-slate-200 shadow-sm hover:bg-slate-50 font-bold gap-2 w-full sm:w-auto">
                          {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                          Gestionar en Stripe
                        </Button>
                      )}
                      
                      {isFree && (
                        <Button onClick={() => navigate('/pricing')} className="bg-blue-600 hover:bg-blue-700 text-white font-bold gap-2 shadow-lg shadow-blue-200 w-full sm:w-auto">
                          🚀 Ver Planes Premium / Pro
                        </Button>
                      )}

                      {/* Botón Sincronizar Pago SIEMPRE visible en Móvil y Desktop */}
                      <Button
                        onClick={handleSyncStatus}
                        disabled={actionLoading}
                        variant="outline"
                        className="bg-white hover:bg-blue-50 border-blue-200 text-blue-600 font-bold text-xs gap-1.5 w-full sm:w-auto shadow-xs h-10 rounded-xl"
                      >
                        {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-blue-600" />}
                        <span>Sincronizar Pago con Stripe</span>
                      </Button>
                    </div>
                  </div>
                </Card>

                {/* Features Grid */}
                <Card className="p-6 sm:p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-6">Funciones de tu Plan</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {getActiveFeatures().map((feature, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-slate-700 font-medium">{feature}</p>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Benefits Section (If Free or Premium) */}
                {(isFree || isPremium) && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 mt-4">
                      <Sparkles className="w-6 h-6 text-purple-600" />
                      <h3 className="text-2xl font-bold text-slate-900">Mejora tu experiencia</h3>
                    </div>
                    <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-6">
                      {isFree && (
                        <Card className="p-6 border-2 border-blue-100 hover:border-blue-300 transition-all cursor-pointer bg-white group" onClick={() => navigate('/pricing')}>
                          <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg"><Crown className="w-6 h-6" /></div>
                            <div>
                              <h4 className="font-bold text-blue-600 uppercase text-xs tracking-widest">Premium</h4>
                              <p className="text-xl font-black text-slate-900">$79/mes</p>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 mb-6">IA financiera, transacciones por voz y chat inteligente ilimitado.</p>
                          <Button className="w-full bg-blue-600 hover:bg-blue-700 group-hover:scale-[1.02] transition-transform">Subir a Premium <ArrowRight className="ml-2 w-4 h-4" /></Button>
                        </Card>
                      )}
                      {(isFree || isPremium) && (
                        <Card className="p-6 border-2 border-purple-100 hover:border-purple-300 transition-all cursor-pointer bg-white group" onClick={() => navigate('/pricing')}>
                          <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center text-white shadow-lg"><Gem className="w-6 h-6" /></div>
                            <div>
                              <h4 className="font-bold text-purple-600 uppercase text-xs tracking-widest">Pro</h4>
                              <p className="text-xl font-black text-slate-900">$129/mes</p>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 mb-6">Reportes PDF, 300 transacciones por voz y soporte prioritario VIP.</p>
                          <Button className="w-full bg-purple-600 hover:bg-purple-700 group-hover:scale-[1.02] transition-transform">Obtener Plan Pro <ArrowRight className="ml-2 w-4 h-4" /></Button>
                        </Card>
                      )}
                    </div>
                  </div>
                )}

                {/* Pro Active Message */}
                {isPro && (
                  <Card className="p-8 bg-gradient-to-br from-purple-900 to-indigo-900 text-white text-center relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                    <div className="relative z-10">
                      <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/30 backdrop-blur-sm">
                        <Gem className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-3xl font-black mb-2 uppercase tracking-tighter">¡Estatus Pro Máximo!</h3>
                      <p className="text-purple-100 mb-6 opacity-90 max-w-md mx-auto">Disfrutas de todas las ventajas exclusivas. Eres parte de nuestra comunidad de élite financiera.</p>
                      <div className="inline-flex items-center gap-2 px-6 py-2 bg-white text-purple-900 rounded-full font-black text-sm uppercase">Acceso VIP Garantizado</div>
                    </div>
                  </Card>
                )}
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <NotificationManager />
              </div>
            )}

            {activeTab === 'support' && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8 pb-10">
                {/* Header de Sección */}
                <div className="text-center space-y-2">
                  <h2 className="text-3xl font-black text-slate-800 tracking-tight">Centro de Ayuda y Feedback</h2>
                  <p className="text-slate-500 max-w-lg mx-auto">¿Cómo podemos ayudarte hoy? Selecciona la opción que mejor se adapte a tu necesidad.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Bloque 1: Soporte Técnico */}
                  <Card className="p-6 border-slate-200 bg-white shadow-sm hover:shadow-md transition-all">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2.5 bg-blue-50 rounded-xl text-blue-600">
                        <Shield size={24} />
                      </div>
                      <h3 className="text-lg font-bold text-slate-800">Soporte Técnico</h3>
                    </div>
                    <div className="space-y-3">
                      <button 
                        onClick={() => navigate('/support?type=bug')}
                        className="w-full flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform"><AlertCircle className="w-4 h-4 text-red-500" /></div>
                          <div className="text-left">
                            <p className="text-sm font-bold text-slate-700">Reportar un Error</p>
                            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Algo no funciona bien</p>
                          </div>
                        </div>
                        <ArrowRight size={16} className="text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                      </button>

                      <button 
                        onClick={() => navigate('/support?type=suggestion')}
                        className="w-full flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform"><Sparkles className="w-4 h-4 text-amber-500" /></div>
                          <div className="text-left">
                            <p className="text-sm font-bold text-slate-700">Sugerir Mejora</p>
                            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Tengo una idea increíble</p>
                          </div>
                        </div>
                        <ArrowRight size={16} className="text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                      </button>
                    </div>
                  </Card>

                  {/* Bloque 2: Feedback y Opinión */}
                  <Card className="p-6 border-slate-200 bg-white shadow-sm hover:shadow-md transition-all">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2.5 bg-purple-50 rounded-xl text-purple-600">
                        <MessageCircle size={24} />
                      </div>
                      <h3 className="text-lg font-bold text-slate-800">Feedback y Opinión</h3>
                    </div>
                    <div className="space-y-3">
                      <button 
                        onClick={() => navigate('/feedback?type=complaint')}
                        className="w-full flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-purple-200 hover:bg-purple-50/30 transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform"><Target className="w-4 h-4 text-purple-500" /></div>
                          <div className="text-left">
                            <p className="text-sm font-bold text-slate-700">Enviar Queja</p>
                            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">No estoy satisfecho</p>
                          </div>
                        </div>
                        <ArrowRight size={16} className="text-slate-300 group-hover:text-purple-500 group-hover:translate-x-1 transition-all" />
                      </button>

                      <button 
                        onClick={() => navigate('/feedback?type=other')}
                        className="w-full flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-purple-200 hover:bg-purple-50/30 transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform"><HelpCircle className="w-4 h-4 text-slate-500" /></div>
                          <div className="text-left">
                            <p className="text-sm font-bold text-slate-700">Otros Asuntos</p>
                            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Cualquier otra duda</p>
                          </div>
                        </div>
                        <ArrowRight size={16} className="text-slate-300 group-hover:text-purple-500 group-hover:translate-x-1 transition-all" />
                      </button>
                    </div>
                  </Card>
                </div>

                {/* Bloque VIP Destacado */}
                <div 
                  className="w-full p-8 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-[2rem] text-white flex flex-col md:flex-row items-center justify-between shadow-xl shadow-blue-200 group cursor-pointer hover:scale-[1.01] transition-all"
                  onClick={() => window.location.href = `mailto:soporte@capacitatu.com?subject=Soporte VIP PGastos - ${user?.username || 'Usuario'}&body=Hola, soy ${user?.username}. Necesito apoyo prioritario con...`}
                >
                  <div className="flex items-center gap-6 mb-4 md:mb-0">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md border border-white/30 shadow-lg group-hover:rotate-6 transition-transform">
                      <Gem className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-2xl font-black tracking-tight">Soporte VIP Prioritario</h3>
                      <p className="text-blue-100 font-medium">Atención directa por correo electrónico.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 px-6 py-3 bg-white text-blue-600 rounded-xl font-bold group-hover:bg-blue-50 transition-colors shadow-lg">
                    Contactar Ahora <ArrowRight size={18} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <Card className="p-6 bg-slate-900 text-white overflow-hidden relative shadow-xl rounded-3xl border border-slate-800">
              <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/15 rounded-full -mr-20 -mt-20 blur-3xl pointer-events-none"></div>
              <div className="flex items-center justify-between mb-5 relative">
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-400" />
                  Radiografía Financiera
                </h3>
                {activitySummary?.streak_days > 0 && (
                  <span className="flex items-center gap-1 text-xs font-extrabold bg-amber-500/20 text-amber-300 px-2.5 py-1 rounded-full border border-amber-500/30 animate-pulse">
                    <Flame className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                    {activitySummary.streak_days}d racha
                  </span>
                )}
              </div>

              {/* Flujo del Mes */}
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 mb-4 border border-white/10">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Flujo del Mes Actual</p>
                <div className="flex items-baseline justify-between mb-3">
                  <span className="text-xs text-slate-300">Balance Neto:</span>
                  <span className={`text-lg font-black ${(activitySummary?.monthly_balance || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    ${(activitySummary?.monthly_balance || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10 text-xs">
                  <div className="flex items-center gap-1.5 text-emerald-300">
                    <ArrowUpRight className="w-3.5 h-3.5" />
                    <div>
                      <p className="text-[10px] text-slate-400">Ingresos</p>
                      <p className="font-bold">+${(activitySummary?.monthly_income || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-rose-300">
                    <ArrowDownRight className="w-3.5 h-3.5" />
                    <div>
                      <p className="text-[10px] text-slate-400">Gastos</p>
                      <p className="font-bold">-${(activitySummary?.monthly_expenses || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Grilla de Métricas Clave */}
              <div className="space-y-3 relative text-xs">
                {/* Metas activas */}
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400"><Target className="w-4 h-4" /></div>
                    <div>
                      <p className="font-bold text-white">{activitySummary?.active_goals_count ?? (stats?.active_goals || 0)} Metas Activas</p>
                      <p className="text-[10px] text-slate-400">Avance promedio global</p>
                    </div>
                  </div>
                  <span className="font-black text-blue-400 text-sm">{activitySummary?.goals_progress_percent || 0}%</span>
                </div>

                {/* Cuentas & Tarjetas */}
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-500/20 rounded-lg text-indigo-400"><Wallet className="w-4 h-4" /></div>
                    <div>
                      <p className="font-bold text-white">Patrimonio Registrado</p>
                      <p className="text-[10px] text-slate-400">{activitySummary?.bank_accounts_count || 0} bancos · {activitySummary?.credit_cards_count || 0} tarjetas</p>
                    </div>
                  </div>
                  <Landmark className="w-4 h-4 text-slate-500" />
                </div>

                {/* Deudas */}
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400"><Scale className="w-4 h-4" /></div>
                    <div>
                      <p className="font-bold text-white">Préstamos / Deudas</p>
                      <p className="text-[10px] text-slate-400">Cobrar: ${(activitySummary?.debts_to_collect || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}</p>
                    </div>
                  </div>
                  <span className="text-[11px] font-bold text-rose-300">
                    Pagar: ${(activitySummary?.debts_to_pay || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                  </span>
                </div>

                {/* Transacciones y Días */}
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div className="p-3 bg-white/5 rounded-xl border border-white/5 text-center">
                    <p className="text-xl font-black text-white leading-tight">{activitySummary?.transaction_count ?? (stats?.transaction_count || 0)}</p>
                    <p className="text-[10px] uppercase font-bold text-slate-400">Movimientos</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-xl border border-white/5 text-center">
                    <p className="text-xl font-black text-white leading-tight">{activitySummary?.days_in_app ?? Math.floor((Date.now() - new Date(user?.created_at || Date.now()).getTime()) / (1000 * 60 * 60 * 24))}</p>
                    <p className="text-[10px] uppercase font-bold text-slate-400">Días en App</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
